import * as React from 'react';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { TextField, Dropdown, IDropdownOption, DatePicker } from 'office-ui-fabric-react';
import { AxiosError } from 'axios';

import { ApiUrls, Permissions } from '@app/AppConstants';
import { DestinationRestrictionGroupModel } from '@app/Models';

import { securityService } from '@app/Services/SecurityService';
import ApiService from '@app/Services/ApiService';
import { PromiseCompletion, CompletionType } from '@app/Classes/PromiseCompletion';
import DateTimeService from '@app/Services/DateTimeService';
import { DateTimePickerService } from '@app/Services/DateTimePickerService';

import { NotificationHandler } from '@app/Components/ToastNotification';
import { ModalWindow, ModalDialogOptions, ModalButtonType, modalService } from '@app/Components/Modal';
import { AzureAuth } from '@app/Components/AzureAuth';

type DestinationGroupDialogProps = {
    personId: string;
};

@observer
export class NewDestinationGroup extends React.Component<DestinationGroupDialogProps, {}> {
    private _store: NewDestinationGroupStore;

    constructor(props: DestinationGroupDialogProps) {
        super(props);
        this._store = new NewDestinationGroupStore();
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        const { hasPermission } = securityService;
        return {
            title: 'New Destination Group',
            buttons: [{
                type: ModalButtonType.Cancel,
                onClick: () => {
                    window.close();
                }
            }, {
                type: ModalButtonType.Save,
                isDisabled: !this._store.isValidForm || !hasPermission(Permissions.EditObjectPersonDestinationRestrictionGroup, !AzureAuth.isReadOnly),
                onClick: async () => {
                    if (await this._store.onSubmit(this.props.personId)){
                        window.close(ModalButtonType.Save);
                    }
                }
            }],
            loader: this._store.loader
        };
    }

    async componentDidMount() {
        await this._store.loadDestinationRestrictionGroup();
    }

    render() {
        return (
            <>
                <Dropdown
                    label="Name"
                    options={this._store.groupOptions}
                    onChange={this._store.onChangeGroup}
                    required={true}
                    selectedKey={this._store.selectedGroupId}
                />
                <DatePicker
                    label="Date From"
                    className={this.dateClassName}
                    allowTextInput={true}
                    formatDate={DateTimePickerService.onFormatDate}
                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                    onSelectDate={this._store.onSelectFromDate}
                    isRequired={true}
                    value={this._store.dateFrom || undefined} />
                {this._store.isInvalidRange && <span className="ms-TextField-errorMessage errorMessage">Invalid date range</span>}
                <DatePicker
                    label="Date To"
                    className={this.dateClassName}
                    allowTextInput={true}
                    formatDate={DateTimePickerService.onFormatDate}
                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                    onSelectDate={this._store.onSelectToDate}
                    value={this._store.dateTo || undefined} />    
                <TextField
                    label="Comment"
                    multiline
                    autoAdjustHeight
                    validateOnFocusOut
                    onChange={this._store.onCommentChange}
                    value={this._store.comment}
                />
                {this._store.validationMessage && <p className="ms-TextField-errorMessage errorMessage">{this._store.validationMessage}</p>}
            </>
        );
    }

    @computed
    get dateClassName() {
        return 'date-field' + (this._store.isInvalidRange ? ' invalid' : '');
    }
}

class NewDestinationGroupStore {
    @observable name: string = '';
    @observable comment: string;
    @observable validationMessage: string | null = '';
    @observable groups: DestinationRestrictionGroupModel[];
    @observable groupsLoaded: PromiseCompletion = new PromiseCompletion(CompletionType.Completed);
    @observable selectedGroupId: string;
    @observable dateFrom: Date | null;
    @observable dateTo: Date | null;
    public loader: PromiseCompletion = new PromiseCompletion();

    @action
    onSelectToDate = (date?: Date | null): void => {
        this.dateTo = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
    };

    @action
    onSelectFromDate = (date?: Date | null): void => {
        this.dateFrom = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
    };

    @computed
    get isInvalidRange() {
        if (!this.dateFrom || !this.dateTo) return false;
        return DateTimeService.isBeforeDate(this.dateTo, this.dateFrom);
    }

    @action
    async loadDestinationRestrictionGroup(){
        const { data: groups } = await ApiService.getTypedData<DestinationRestrictionGroupModel[]>(ApiUrls.DestinationRestrictionGroups, null, {completion: this.loader});
        this.groups = groups;
    }

    @action
    onChangeGroup = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        this.selectedGroupId = option && option.key as string || '';
    };

    @action
    onNameChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        this.name = newValue || '';
    };
    @action
    onCommentChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        this.comment = newValue || '';
    };

    @action.bound
    async onSubmit(personId: string) {
        try{
            const params = {
                personId: personId,
                destinationRestrictionGroupId: this.selectedGroupId,
                dateFrom: this.dateFrom,
                dateTo: this.dateTo,
                comment: this.comment
            };
            const { data } = await ApiService.postData(ApiUrls.PersonDestinationRestrictionGroupUrl, params);
            if (!data.hasErrors) {
                this.validationMessage = null;
                NotificationHandler.showSuccess('New destination group was added', 'Success message');
                return true;
            } else {
                this.validationMessage = data.errors.join(' ');
            }
        } catch (ex) {
            modalService.showError(ex as AxiosError);
        }

        return false;
    }

    @computed
    get isValidForm() {
        return this.dateFrom && this.selectedGroupId;
    }

    @computed
    get groupOptions(){
        return this.groups && this.groups.map(key =>{
            return {key: key.id, text: key.name};
        });
    }
}